import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  MagnifyingGlass,
  SpinnerGap,
  Link as LinkIcon,
  LinkBreak,
  CaretLeft,
  CaretRight,
  CheckCircle,
  Warning,
  X,
  Plus,
  Trash,
  Book,
  Funnel,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'alertness', name: 'Alertness' },
  { id: 'attitude', name: 'Attitude' },
  { id: 'safety', name: 'Safety & Your Vehicle' },
  { id: 'safety_margins', name: 'Safety Margins' },
  { id: 'hazard_awareness', name: 'Hazard Awareness' },
  { id: 'vulnerable_road_users', name: 'Vulnerable Road Users' },
  { id: 'other_conditions', name: 'Other Conditions' },
  { id: 'vehicle_documents', name: 'Vehicle Documents' },
  { id: 'incidents_accidents', name: 'Incidents & Accidents' },
  { id: 'vehicle_loading', name: 'Vehicle Loading' },
  { id: 'road_signs', name: 'Road Signs' },
  { id: 'motorway_rules', name: 'Motorway Rules' },
];

// Rule search modal for linking
const RuleSearchModal = ({ onSelect, onClose, existingRules }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/admin/highway-code/rules-search`, {
        params: { q }, withCredentials: true
      });
      // Filter out already-linked rules
      const existingIds = new Set(existingRules.map(r => `${r.section_id}:${r.rule_id}`));
      setResults((data.results || []).filter(r => !existingIds.has(`${r.section_id}:${r.rule_id}`)));
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, [existingRules]);

  const handleInput = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start justify-center pt-[15vh]" onClick={onClose}>
      <div className="bg-card border border-border rounded-sm w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()} data-testid="rule-search-modal">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <MagnifyingGlass className="w-4 h-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => handleInput(e.target.value)}
            placeholder="Search rules by number, keyword (e.g. 'Rule 167', 'speed limit')..."
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
            data-testid="rule-search-input"
          />
          {searching && <SpinnerGap className="w-4 h-4 text-accent animate-spin" />}
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {results.length === 0 && query.length >= 2 && !searching && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">No matching rules found</div>
          )}
          {results.map((rule, i) => (
            <button
              key={i}
              onClick={() => { onSelect(rule); onClose(); }}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-muted/50 transition-colors border-b border-border/30 last:border-0"
              data-testid={`rule-result-${rule.rule_id}`}
            >
              <span className="text-[10px] font-mono font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded-sm flex-shrink-0">
                {rule.rule_id}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{rule.title}</p>
                <p className="text-[10px] text-muted-foreground truncate">{rule.section_id}</p>
              </div>
              <Plus className="w-4 h-4 text-success flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};


// Question row with linked rules
const QuestionRow = ({ question, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const rules = question.linked_rules || [];

  const handleAddRule = async (rule) => {
    const newRules = [...rules, rule];
    await saveLinks(question.id, newRules);
  };

  const handleRemoveRule = async (index) => {
    const newRules = rules.filter((_, i) => i !== index);
    await saveLinks(question.id, newRules);
  };

  const saveLinks = async (questionId, newRules) => {
    setSaving(true);
    setMsg(null);
    try {
      await axios.put(`${API}/admin/question-links/${questionId}`, { rules: newRules }, { withCredentials: true });
      onUpdate(questionId, newRules);
      setMsg({ type: 'success', text: 'Links saved' });
      setTimeout(() => setMsg(null), 2000);
    } catch {
      setMsg({ type: 'error', text: 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn("border border-border rounded-sm transition-all", expanded ? "bg-card" : "bg-card/50")} data-testid={`question-row-${question.id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-3 flex items-center gap-3"
      >
        <div className={cn(
          "w-2 h-2 rounded-full flex-shrink-0",
          rules.length > 0 ? "bg-success" : "bg-muted-foreground/30"
        )} />
        <div className="min-w-0 flex-1">
          <p className="text-sm truncate">{question.question_text}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-sm">{question.category}</span>
            {rules.length > 0 && (
              <span className="text-[10px] text-success flex items-center gap-0.5">
                <LinkIcon weight="bold" className="w-2.5 h-2.5" /> {rules.length} rule{rules.length > 1 ? 's' : ''}
              </span>
            )}
            {rules.length === 0 && (
              <span className="text-[10px] text-muted-foreground/50 flex items-center gap-0.5">
                <LinkBreak weight="bold" className="w-2.5 h-2.5" /> unlinked
              </span>
            )}
          </div>
        </div>
        <CaretRight weight="bold" className={cn("w-4 h-4 text-muted-foreground transition-transform", expanded && "rotate-90")} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border/50 pt-3 space-y-3">
          {/* Question details */}
          <div>
            <p className="text-xs text-muted-foreground mb-1 font-bold uppercase tracking-wider">Question</p>
            <p className="text-sm">{question.question_text}</p>
          </div>
          {question.explanation && (
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-bold uppercase tracking-wider">Explanation</p>
              <p className="text-xs text-muted-foreground">{question.explanation}</p>
            </div>
          )}

          {/* Linked rules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Linked Highway Code Rules</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowSearch(true)}
                className="h-7 text-xs rounded-sm"
                data-testid="add-rule-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Add Rule
              </Button>
            </div>

            {rules.length === 0 && (
              <p className="text-xs text-muted-foreground/50 italic py-2">No rules linked. Click "Add Rule" to search and link a Highway Code rule.</p>
            )}

            <div className="space-y-1.5">
              {rules.map((rule, i) => (
                <div key={i} className="flex items-center gap-2 bg-muted/30 border border-border/50 rounded-sm px-3 py-2">
                  <Book weight="fill" className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                  <span className="text-[10px] font-mono font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded-sm flex-shrink-0">
                    {rule.rule_id}
                  </span>
                  <span className="text-xs flex-1 truncate">{rule.title || rule.rule_id}</span>
                  <span className="text-[10px] text-muted-foreground truncate max-w-[120px]">{rule.section_id}</span>
                  <button
                    onClick={() => handleRemoveRule(i)}
                    className="text-destructive/60 hover:text-destructive p-0.5 rounded-sm hover:bg-destructive/10 flex-shrink-0"
                    data-testid={`remove-rule-${rule.rule_id}`}
                  >
                    <Trash weight="bold" className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            {msg && (
              <div className={cn("mt-2 text-xs flex items-center gap-1", msg.type === 'success' ? "text-success" : "text-destructive")}>
                {msg.type === 'success' ? <CheckCircle className="w-3 h-3" /> : <Warning className="w-3 h-3" />}
                {msg.text}
              </div>
            )}
            {saving && <SpinnerGap className="w-4 h-4 text-accent animate-spin mt-2" />}
          </div>
        </div>
      )}

      {showSearch && (
        <RuleSearchModal
          existingRules={rules}
          onSelect={handleAddRule}
          onClose={() => setShowSearch(false)}
        />
      )}
    </div>
  );
};


export const AdminRuleLinksPage = () => {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [relinkLoading, setRelinkLoading] = useState(false);
  const [relinkMsg, setRelinkMsg] = useState(null);
  const limit = 15;
  const searchTimeout = useRef(null);

  useEffect(() => {
    fetchQuestions();
  }, [page, categoryFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * limit, limit });
      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      const { data } = await axios.get(`${API}/admin/question-links?${params}`, { withCredentials: true });
      setQuestions(data.questions || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setPage(0); fetchQuestions(); }, 400);
  };

  const handleUpdateLinks = (questionId, newRules) => {
    setQuestions(prev => prev.map(q =>
      q.id === questionId ? { ...q, linked_rules: newRules } : q
    ));
  };

  const handleBulkRelink = async () => {
    setRelinkLoading(true);
    setRelinkMsg(null);
    try {
      await axios.post(`${API}/admin/highway-code/link-questions`, {}, { withCredentials: true });
      setRelinkMsg({ type: 'success', text: 'Auto-linking started in background. Refresh in a few seconds to see results.' });
      setTimeout(() => fetchQuestions(), 5000);
    } catch {
      setRelinkMsg({ type: 'error', text: 'Failed to start auto-linking.' });
    } finally {
      setRelinkLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const linkedCount = questions.filter(q => q.linked_rules?.length > 0).length;

  return (
    <div className="p-6" data-testid="admin-rule-links-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Book weight="fill" className="w-5 h-5 text-accent" />
            <h1 className="font-heading font-bold text-xl">Question ↔ Highway Code Links</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Review and manually adjust which Highway Code rules are linked to each question.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleBulkRelink}
          disabled={relinkLoading}
          className="rounded-sm"
          data-testid="auto-relink-btn"
        >
          {relinkLoading ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <ArrowsClockwise className="w-4 h-4 mr-2" />}
          Auto Re-link All
        </Button>
      </div>

      {relinkMsg && (
        <div className={cn("p-3 rounded-sm border mb-4 text-sm flex items-center gap-2",
          relinkMsg.type === 'success' ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"
        )}>
          {relinkMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <Warning className="w-4 h-4" />}
          {relinkMsg.text}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search questions..."
            className="w-full bg-card border border-border rounded-sm pl-9 pr-4 py-2 text-sm"
            data-testid="search-questions"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm"
          data-testid="category-filter"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm"
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          <option value="linked">Linked</option>
          <option value="unlinked">Unlinked</option>
        </select>
        <div className="text-xs text-muted-foreground ml-auto">
          Showing {questions.length} of {total} · <span className="text-success">{linkedCount} linked</span>
        </div>
      </div>

      {/* Question List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <SpinnerGap className="w-8 h-8 text-accent animate-spin" />
        </div>
      ) : (
        <div className="space-y-2" data-testid="questions-list">
          {questions.map(q => (
            <QuestionRow key={q.id} question={q} onUpdate={handleUpdateLinks} />
          ))}
          {questions.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No questions found matching your filters.
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="rounded-sm"
            data-testid="prev-page"
          >
            <CaretLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="rounded-sm"
            data-testid="next-page"
          >
            Next <CaretRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};
